import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Ticket, X, ExternalLink, Loader2, CheckCircle2, ArrowLeft,
  Wifi, Zap, Copy, Sparkles,
} from "lucide-react";

/**
 * Multi-step Syncro linker modal — used from ClientDrawer's "Find in
 * Syncro" button. Steps:
 *
 *   1. ``search``  — name search against Syncro, pick a customer
 *   2. ``tickets`` — list that customer's tickets, pick the install one
 *   3. ``extract`` — regex-extract NBN creds from the ticket body,
 *                     show a review checkbox list
 *   4. ``done``    — save (link + selected fields) then close
 *
 * The parent owns nothing — we call ``onLinked(fresh)`` with the newly
 * updated customer doc so the drawer can refresh in place.
 */
export default function SyncroLinkerModal({ customer, onClose, onLinked }) {
  const [step, setStep] = useState("search");
  const [query, setQuery] = useState(customer.business_name || customer.contact_name || "");
  const [matches, setMatches] = useState(null);
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [pickedTicket, setPickedTicket] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [busy, setBusy] = useState(false);

  // Auto-run the initial search so the operator sees candidates on open.
  useEffect(() => { runSearch(); }, []);

  async function runSearch() {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/commissions/customers/${customer.id}/syncro/search-customers`,
        { params: query ? { query } : {} }
      );
      setMatches(data.matches || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Syncro search failed");
    } finally { setBusy(false); }
  }

  async function pickCustomer(m) {
    setPickedCustomer(m);
    setStep("tickets");
    setBusy(true);
    try {
      const { data } = await api.get(
        `/commissions/customers/${customer.id}/syncro/tickets`,
        { params: { syncro_customer_id: m.id } }
      );
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ticket list failed");
    } finally { setBusy(false); }
  }

  async function pickTicket(t) {
    setPickedTicket(t);
    setStep("extract");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/customers/${customer.id}/syncro/extract-nbn`,
        { syncro_ticket_id: t.id },
      );
      setExtracted(data.extracted || {});
      // Pre-tick every extracted field — operator un-ticks anything wrong.
      const preAccepted = {};
      Object.entries(data.extracted || {}).forEach(([k, v]) => {
        if (v) preAccepted[k] = true;
      });
      setAccepted(preAccepted);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "NBN extraction failed");
    } finally { setBusy(false); }
  }

  async function saveLink() {
    setBusy(true);
    try {
      const body = {
        syncro_customer_id: pickedCustomer.id,
        syncro_ticket_id: pickedTicket?.id || null,
        syncro_ticket_number: pickedTicket?.number ? String(pickedTicket.number) : null,
      };
      // Only send fields the operator ticked.
      ["nbn_username", "nbn_password", "nbn_wsm_service_id", "nbn_speed"].forEach((k) => {
        if (accepted[k] && extracted?.[k]) body[k] = extracted[k];
      });
      const { data } = await api.post(
        `/commissions/customers/${customer.id}/syncro/link`,
        body,
      );
      const written = Object.keys(body).filter((k) => k.startsWith("nbn_")).length;
      toast.success(
        `Linked to Syncro #${pickedTicket?.number || pickedTicket?.id || pickedCustomer.id}`
        + (written ? ` — filled ${written} NBN field${written === 1 ? "" : "s"}` : "")
      );
      onLinked(data.client);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Link save failed");
    } finally { setBusy(false); }
  }

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="syncro-linker-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-slate-800 flex items-center gap-3">
          {step !== "search" && (
            <button
              type="button"
              onClick={() => {
                if (step === "tickets") setStep("search");
                else if (step === "extract") setStep("tickets");
              }}
              className="p-1.5 rounded hover:bg-slate-800 text-slate-400"
              data-testid="syncro-linker-back"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
              {step === "search"  && "Step 1 · Pick Syncro customer"}
              {step === "tickets" && "Step 2 · Pick install ticket"}
              {step === "extract" && "Step 3 · Review extracted details"}
            </div>
            <div className="text-sm font-semibold text-slate-100 truncate">
              {customer.business_name || customer.contact_name || "(no name)"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-800 text-slate-400"
            data-testid="syncro-linker-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {/* ---------- Step 1 · customer search ---------- */}
          {step === "search" && (
            <div className="p-5 space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                    data-testid="syncro-linker-search-input"
                    className="w-full rounded-lg border border-slate-800 bg-slate-950 pl-9 pr-3 py-2.5 text-sm text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                    placeholder="Search Syncro by name…"
                    autoFocus
                  />
                </div>
                <button
                  onClick={runSearch}
                  disabled={busy}
                  data-testid="syncro-linker-search-btn"
                  className="px-4 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                </button>
              </div>
              {matches === null ? (
                <div className="text-slate-500 text-sm">Loading…</div>
              ) : matches.length === 0 ? (
                <div className="text-slate-500 text-sm">No matches. Try a shorter name.</div>
              ) : (
                <ul className="space-y-2" data-testid="syncro-linker-matches">
                  {matches.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => pickCustomer(m)}
                        data-testid={`syncro-linker-pick-customer-${m.id}`}
                        className="w-full text-left rounded-lg border border-slate-800 hover:border-cyan-500/40 bg-slate-950/40 hover:bg-slate-950 p-3 transition-colors"
                      >
                        <div className="text-sm font-semibold text-slate-100">
                          {m.business_name || m.fullname || `#${m.id}`}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {[m.email, m.phone, [m.city, m.state].filter(Boolean).join(" ")]
                            .filter(Boolean).join(" · ")}
                        </div>
                        <div className="text-[10px] font-mono text-slate-600 mt-1">Syncro id {m.id}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ---------- Step 2 · ticket picker ---------- */}
          {step === "tickets" && (
            <div className="p-5 space-y-4">
              <div className="text-xs text-slate-400">
                Linked to <b className="text-slate-200">{pickedCustomer?.business_name || pickedCustomer?.fullname}</b>.
                Pick the install ticket — usually the earliest / most-detailed one.
              </div>
              {tickets === null ? (
                <div className="text-slate-500 text-sm">Loading tickets…</div>
              ) : tickets.length === 0 ? (
                <div className="text-slate-500 text-sm">
                  No tickets found for this Syncro customer. You can skip and just link the customer record.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="syncro-linker-tickets">
                  {tickets.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => pickTicket(t)}
                        data-testid={`syncro-linker-pick-ticket-${t.id}`}
                        className="w-full text-left rounded-lg border border-slate-800 hover:border-cyan-500/40 bg-slate-950/40 hover:bg-slate-950 p-3 transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <Ticket className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-100">
                              <span className="font-mono text-cyan-300">#{t.number}</span>
                              {" · "}{t.subject}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {[t.status, t.problem_type, t.created_at?.slice(0, 10)]
                                .filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setPickedTicket(null);
                    setExtracted({});
                    setAccepted({});
                    setStep("extract");
                  }}
                  data-testid="syncro-linker-skip-tickets"
                  className="text-xs text-slate-400 hover:text-cyan-300"
                >
                  Skip ticket — just link the Syncro customer record →
                </button>
              </div>
            </div>
          )}

          {/* ---------- Step 3 · review extracted ---------- */}
          {step === "extract" && (
            <div className="p-5 space-y-5">
              {pickedTicket && (
                <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-xs">
                  <div className="flex items-center gap-2 text-slate-300 font-semibold">
                    <Ticket className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="font-mono text-cyan-300">#{pickedTicket.number}</span>
                    <span className="truncate">{pickedTicket.subject}</span>
                  </div>
                </div>
              )}
              {busy ? (
                <div className="text-slate-500 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Scanning ticket for NBN details…
                </div>
              ) : extracted && Object.values(extracted).some(Boolean) ? (
                <div className="space-y-2" data-testid="syncro-linker-extracted">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-amber-400" /> Extracted — un-tick anything wrong
                  </div>
                  {[
                    ["nbn_username", "Username", Wifi],
                    ["nbn_password", "Password", Wifi],
                    ["nbn_wsm_service_id", "Service ID", Wifi],
                    ["nbn_speed", "Speed", Zap],
                  ].map(([field, label, Icon]) => {
                    const value = extracted[field];
                    if (!value) return null;
                    const on = !!accepted[field];
                    return (
                      <label
                        key={field}
                        data-testid={`syncro-linker-field-${field}`}
                        className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                          on ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-800 bg-slate-950/40"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => setAccepted((a) => ({ ...a, [field]: e.target.checked }))}
                          className="w-4 h-4 accent-emerald-500"
                        />
                        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
                          <div className="text-sm font-mono text-slate-100 truncate">
                            {field === "nbn_password" ? "•".repeat(Math.min(value.length, 16)) : value}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="text-slate-500 text-sm">
                  No NBN details recognised in the ticket. You can still link the record — paste the details manually into the drawer after saving.
                </div>
              )}
              <div className="pt-3 border-t border-slate-800 flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveLink}
                  disabled={busy}
                  data-testid="syncro-linker-save"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {busy ? "Saving…" : "Link + save"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
